/**
 * CitationLink - parse VN legal citations va linkify voi multi-source fallback dropdown.
 *
 * Inputs: LLM streaming text co the chua citation kieu:
 *   - "Dieu 8 Khoan 2 Luat Lao dong 2019 (so 45/2019/QH14)"
 *   - "Bo luat Dan su 2015 (so 91/2015/QH13)"
 *   - "Nghi dinh so 13/2023/ND-CP"
 *   - "Thong tu so 78/2021/TT-BTC"
 *   - "Nghi quyet so 03/2021/NQ-HDTP"
 *   - "An le so 02/2016/AL"
 *   - "Hien phap 2013"
 *
 * Output: ReactNode array - plain text segment + <a> for each citation, voi
 * 1 chevron button mo dropdown 6 nguon tra cuu fallback.
 *
 * Security: KHONG dung dangerouslySetInnerHTML. Render bang React.createElement
 * de browser auto-escape moi text segment (immune voi XSS tu LLM output).
 *
 * URL pattern verification (2026-05-11):
 *   - vbpl.vn search: 200 OK, working search.
 *   - chinhphu.vn ?searchString=: 200 nhung tra ve homepage -> Google site fallback.
 *   - moj.gov.vn tim-kiem.aspx: 404 -> Google site fallback.
 *   - phapluat.gov.vn ?q=: ambiguous -> Google site fallback.
 *   - congbao.chinhphu.vn direct pattern: khong ban so cu the -> Google site fallback.
 *   - Google site:gov.vn: always 200 + reliable result.
 *
 * Spec: F-FE-CHAT M1 citation linkify TASK 2026-05-11 (multi-source fallback).
 */

import React from 'react';

// ---- Types ----

export type CitationType =
  | 'LUAT'
  | 'BO_LUAT'
  | 'ND'
  | 'TT'
  | 'NQ'
  | 'AN_LE'
  | 'HIEN_PHAP'
  | 'UNKNOWN';

export interface CitationParsed {
  raw: string;
  type: CitationType;
  doc_number?: string;
  doc_name?: string;
  dieu?: string;
  khoan?: string;
  vbpl_url: string;
}

// ---- Source providers (multi-source fallback) ----

export type SourceId =
  | 'vbpl'
  | 'chinhphu'
  | 'congbao'
  | 'moj'
  | 'phapluat'
  | 'google';

export interface SourceProvider {
  id: SourceId;
  name: string;
  descriptor: string;
  buildUrl: (docNum: string, docName?: string) => string;
  priority: number;
}

const googleSite = (domain: string, docNum: string): string =>
  `https://www.google.com/search?q=${encodeURIComponent(`site:${domain} "${docNum}"`)}`;

// Module-scope const - NEVER re-created moi render.
export const SOURCES: SourceProvider[] = [
  {
    id: 'vbpl',
    name: 'vbpl.moj.gov.vn',
    descriptor: 'Co so du lieu Quoc gia ve VBPL (Bo Tu phap)',
    buildUrl: (docNum) =>
      `https://vbpl.vn/van-ban/trung-uong?keyword=${encodeURIComponent(docNum)}`,
    priority: 1,
  },
  {
    id: 'chinhphu',
    name: 'chinhphu.vn',
    descriptor: 'Cong thong tin dien tu Chinh phu',
    // Direct ?searchString= chi tra ve homepage; dung Google site fallback.
    buildUrl: (docNum) => googleSite('chinhphu.vn', docNum),
    priority: 2,
  },
  {
    id: 'congbao',
    name: 'congbao.chinhphu.vn',
    descriptor: 'Cong bao Chinh phu chinh thuc',
    // Direct pattern khong on dinh; Google site:congbao.chinhphu.vn reliable.
    buildUrl: (docNum) => googleSite('congbao.chinhphu.vn', docNum),
    priority: 3,
  },
  {
    id: 'moj',
    name: 'moj.gov.vn',
    descriptor: 'Bo Tu phap',
    // tim-kiem.aspx tra ve 404; dung Google site fallback.
    buildUrl: (docNum) => googleSite('moj.gov.vn', docNum),
    priority: 4,
  },
  {
    id: 'phapluat',
    name: 'phapluat.gov.vn',
    descriptor: 'Cong thong tin phap luat quoc gia',
    buildUrl: (docNum) => googleSite('phapluat.gov.vn', docNum),
    priority: 5,
  },
  {
    id: 'google',
    name: 'Google',
    descriptor: 'Tim tren cac trang .gov.vn',
    buildUrl: (docNum) =>
      `https://www.google.com/search?q=${encodeURIComponent(`site:gov.vn "${docNum}"`)}`,
    priority: 6,
  },
];

// ---- URL builder (legacy primary) ----

const VBPL_BASE = 'https://vbpl.vn/van-ban/trung-uong';

export function buildVbplUrl(keyword: string): string {
  return `${VBPL_BASE}?keyword=${encodeURIComponent(keyword)}`;
}

/** Helper for unit test - lookup source by id va build URL. */
export function buildSourceUrl(
  id: SourceId,
  docNum: string,
  docName?: string
): string {
  const src = SOURCES.find((s) => s.id === id);
  if (!src) throw new Error(`Unknown source: ${id}`);
  return src.buildUrl(docNum, docName);
}

// ---- Regex patterns ----

interface PatternDef {
  type: CitationType;
  re: RegExp;
  extract: (m: RegExpMatchArray) => Omit<CitationParsed, 'vbpl_url'> & { keyword: string };
}

const PATTERNS: PatternDef[] = [
  {
    type: 'LUAT',
    re: /(?:Điều|Dieu)\s+(\d+[a-z]?)(?:\s*,?\s*(?:Khoản|Khoan)\s+(\d+))?\s+(Bộ\s+luật|Luật|Bo\s+luat|Luat)\s+([^()\[\],;\n]{2,80}?)(?:\s*\(\s*s[ốo]\s+(\d+\/\d{4}\/[A-Z][A-Z0-9-]+)\s*\))/g,
    extract: (m) => {
      const dieu = m[1];
      const khoan = m[2];
      const luatType = m[3];
      const docName = m[4].trim();
      const docNumber = m[5];
      const type: CitationType = /Bộ|Bo/.test(luatType) ? 'BO_LUAT' : 'LUAT';
      return {
        raw: m[0],
        type,
        doc_number: docNumber,
        doc_name: `${luatType} ${docName}`,
        dieu,
        khoan,
        keyword: docNumber,
      };
    },
  },
  {
    type: 'LUAT',
    re: /(Bộ\s+luật|Luật|Bo\s+luat|Luat)\s+([^()\[\],;\n]{2,80}?)(?:\s*\(\s*s[ốo]\s+(\d+\/\d{4}\/[A-Z][A-Z0-9-]+)\s*\))/g,
    extract: (m) => {
      const luatType = m[1];
      const docName = m[2].trim();
      const docNumber = m[3];
      const type: CitationType = /Bộ|Bo/.test(luatType) ? 'BO_LUAT' : 'LUAT';
      return {
        raw: m[0],
        type,
        doc_number: docNumber,
        doc_name: `${luatType} ${docName}`,
        keyword: docNumber,
      };
    },
  },
  {
    type: 'ND',
    re: /(?:Nghị\s+định|Nghi\s+dinh)\s+(?:s[ốo]\s+)?(\d+\/\d{4}\/N[ĐD][\s-]?CP)/g,
    extract: (m) => {
      const docNumber = m[1].replace(/\s/g, '-').replace('Đ', 'D');
      return {
        raw: m[0],
        type: 'ND',
        doc_number: docNumber,
        keyword: docNumber,
      };
    },
  },
  {
    type: 'TT',
    re: /(?:Thông\s+tư|Thong\s+tu)\s+(?:s[ốo]\s+)?(\d+\/\d{4}\/TT[\s-]?[A-Z]+)/g,
    extract: (m) => {
      const docNumber = m[1].replace(/\s/g, '-');
      return {
        raw: m[0],
        type: 'TT',
        doc_number: docNumber,
        keyword: docNumber,
      };
    },
  },
  {
    type: 'NQ',
    re: /(?:Nghị\s+quyết|Nghi\s+quyet)\s+(?:s[ốo]\s+)?(\d+\/\d{4}\/NQ[\s-]?(?:CP|H[ĐD]TP))/g,
    extract: (m) => {
      const docNumber = m[1].replace(/\s/g, '-').replace('Đ', 'D');
      return {
        raw: m[0],
        type: 'NQ',
        doc_number: docNumber,
        keyword: docNumber,
      };
    },
  },
  {
    type: 'AN_LE',
    re: /(?:Án\s+lệ|An\s+le)\s+(?:s[ốo]\s+)?(\d+\/\d{4}\/AL)/g,
    extract: (m) => {
      const docNumber = m[1];
      return {
        raw: m[0],
        type: 'AN_LE',
        doc_number: docNumber,
        keyword: docNumber,
      };
    },
  },
  {
    type: 'HIEN_PHAP',
    re: /(?:Hiến\s+pháp|Hien\s+phap)\s+(\d{4})/g,
    extract: (m) => {
      const year = m[1];
      return {
        raw: m[0],
        type: 'HIEN_PHAP',
        doc_name: `Hien phap ${year}`,
        keyword: `Hien phap ${year}`,
      };
    },
  },
];

// ---- Parser ----

interface MatchRecord {
  start: number;
  end: number;
  parsed: CitationParsed;
}

function findAllMatches(text: string): MatchRecord[] {
  const records: MatchRecord[] = [];
  for (const pat of PATTERNS) {
    const re = new RegExp(pat.re.source, pat.re.flags);
    let m: RegExpMatchArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index === undefined) continue;
      const extracted = pat.extract(m);
      records.push({
        start: m.index,
        end: m.index + m[0].length,
        parsed: {
          raw: extracted.raw,
          type: extracted.type,
          doc_number: extracted.doc_number,
          doc_name: extracted.doc_name,
          dieu: extracted.dieu,
          khoan: extracted.khoan,
          vbpl_url: buildVbplUrl(extracted.keyword),
        },
      });
    }
  }
  records.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const out: MatchRecord[] = [];
  let cursor = 0;
  for (const r of records) {
    if (r.start < cursor) continue;
    out.push(r);
    cursor = r.end;
  }
  return out;
}

export interface ParseResult {
  segments: Array<
    | { kind: 'text'; text: string }
    | { kind: 'citation'; citation: CitationParsed }
    | { kind: 'domain'; domain: string; href: string }
  >;
  citations: CitationParsed[];
}

// Whitelist of VN gov domains to auto-link (master prompt v1.1 Block 6 priority list).
const DOMAIN_HREF_MAP: Record<string, string> = {
  // Canonical token emitted by master-prompt-v2 (P0-4) is vbpl.moj.gov.vn.
  'vbpl.moj.gov.vn': 'https://vbpl.moj.gov.vn/',
  // Legacy backward-compat key: older model output / cached answers may still
  // emit the bare "vbpl.vn" token; keep it mapped so those links still resolve.
  'vbpl.vn': 'https://vbpl.vn/',
  'chinhphu.vn': 'https://chinhphu.vn/',
  'congbao.chinhphu.vn': 'https://congbao.chinhphu.vn/',
  'moj.gov.vn': 'https://moj.gov.vn/',
  'phapluat.gov.vn': 'https://phapluat.gov.vn/',
  'baophapluat.vn': 'https://baophapluat.vn/',
  'plo.vn': 'https://plo.vn/',
  'kiemsat.vn': 'https://kiemsat.vn/',
  'nld.com.vn': 'https://nld.com.vn/',
  'tuoitre.vn': 'https://tuoitre.vn/',
  'vnexpress.net': 'https://vnexpress.net/',
  'vietnamnet.vn': 'https://vietnamnet.vn/',
  'vietnamplus.vn': 'https://vietnamplus.vn/',
  'tienphong.vn': 'https://tienphong.vn/',
};
const DOMAIN_REGEX = new RegExp(
  '\\b(' + Object.keys(DOMAIN_HREF_MAP)
    .sort((a, b) => b.length - a.length)
    .map((d) => d.replace(/\./g, '\\.'))
    .join('|') + ')\\b',
  'gi'
);

function splitTextByDomain(text: string): ParseResult['segments'] {
  const out: ParseResult['segments'] = [];
  let last = 0;
  DOMAIN_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DOMAIN_REGEX.exec(text)) !== null) {
    if (m.index > last) {
      out.push({ kind: 'text', text: text.slice(last, m.index) });
    }
    const domain = m[1].toLowerCase();
    const href = DOMAIN_HREF_MAP[domain] || `https://${domain}/`;
    out.push({ kind: 'domain', domain: m[1], href });
    last = m.index + m[1].length;
  }
  if (last < text.length) {
    out.push({ kind: 'text', text: text.slice(last) });
  }
  return out.length > 0 ? out : [{ kind: 'text', text }];
}

export function parseCitations(text: string): ParseResult {
  if (!text) return { segments: [], citations: [] };
  const matches = findAllMatches(text);
  const segments: ParseResult['segments'] = [];
  let last = 0;
  for (const m of matches) {
    if (m.start > last) {
      // Apply domain auto-link to text BETWEEN citations.
      for (const seg of splitTextByDomain(text.slice(last, m.start))) {
        segments.push(seg);
      }
    }
    segments.push({ kind: 'citation', citation: m.parsed });
    last = m.end;
  }
  if (last < text.length) {
    for (const seg of splitTextByDomain(text.slice(last))) {
      segments.push(seg);
    }
  }
  return { segments, citations: matches.map((m) => m.parsed) };
}

// ---- Component ----

const linkStyle: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'underline',
  textDecorationColor: '#1B3B6F',
  textUnderlineOffset: '2px',
  textDecorationThickness: '2px',
  cursor: 'pointer',
  borderRadius: 2,
  padding: '0 1px',
  transition: 'background-color 120ms ease',
};

const wrapStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
};

const toggleStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  marginLeft: 1,
  padding: 0,
  border: 0,
  background: 'transparent',
  color: '#1B3B6F',
  cursor: 'pointer',
  fontSize: 11,
  lineHeight: 1,
  borderRadius: 3,
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  background: '#FFFFFF',
  color: '#111827',
  border: '1px solid #E5E7EB',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  borderRadius: 8,
  zIndex: 50,
  minWidth: 280,
  padding: '4px 0',
};

const itemStyle: React.CSSProperties = {
  display: 'block',
  padding: '8px 12px',
  textDecoration: 'none',
  color: 'inherit',
  fontSize: 13,
  lineHeight: 1.4,
};

const itemNameStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  color: '#1B3B6F',
};

const itemDescStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#6B7280',
  marginTop: 1,
};

export interface CitationClickPayload {
  citation: CitationParsed;
  source_id: SourceId;
}

interface CitationItemProps {
  citation: CitationParsed;
  defaultOpen?: boolean;
  onCitationClick?: (payload: CitationClickPayload) => void;
}

function CitationItem({
  citation,
  defaultOpen = false,
  onCitationClick,
}: CitationItemProps): React.ReactElement {
  const [open, setOpen] = React.useState<boolean>(defaultOpen);
  const wrapRef = React.useRef<HTMLSpanElement | null>(null);

  // Click-away + ESC close.
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (!wrapRef.current) return;
      if (e.target instanceof Node && wrapRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const docNum = citation.doc_number ?? '';
  const docName = citation.doc_name;
  const primary = SOURCES[0];

  return React.createElement(
    'span',
    {
      ref: wrapRef,
      className: 'citation-link-wrap',
      style: wrapStyle,
    },
    React.createElement(
      'a',
      {
        href: primary.buildUrl(docNum || (citation.doc_name ?? ''), docName),
        target: '_blank',
        rel: 'noopener noreferrer',
        className: 'citation-link',
        title: 'Mo van ban tai vbpl.moj.gov.vn (AI tu sinh, can xac minh)',
        'aria-label': `Mo ${citation.raw} tai vbpl.moj.gov.vn trong tab moi (AI tu sinh, can xac minh)`,
        style: linkStyle,
        onClick: () => {
          if (onCitationClick) {
            onCitationClick({ citation, source_id: 'vbpl' });
          }
        },
        onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.currentTarget.style.backgroundColor = '#FEF3C7';
        },
        onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        },
      },
      citation.raw
    ),
    React.createElement(
      'button',
      {
        type: 'button',
        className: 'citation-dropdown-toggle',
        'aria-label': 'Tim tren nguon khac',
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        style: toggleStyle,
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        },
      },
      // ASCII-safe chevron glyph "v" (lowercase) avoid AI typographic symbol.
      React.createElement('span', { 'aria-hidden': 'true' }, 'v')
    ),
    open
      ? React.createElement(
          'div',
          {
            className: 'citation-dropdown',
            role: 'menu',
            style: dropdownStyle,
          },
          ...SOURCES.map((src) =>
            React.createElement(
              'a',
              {
                key: src.id,
                href: src.buildUrl(docNum || (citation.doc_name ?? ''), docName),
                target: '_blank',
                rel: 'noopener noreferrer',
                role: 'menuitem',
                className: 'citation-dropdown-item',
                style: itemStyle,
                'data-source-id': src.id,
                onClick: () => {
                  setOpen(false);
                  if (onCitationClick) {
                    onCitationClick({ citation, source_id: src.id });
                  }
                },
              },
              React.createElement('span', { style: itemNameStyle }, src.name),
              React.createElement('span', { style: itemDescStyle }, src.descriptor)
            )
          )
        )
      : null
  );
}

interface Props {
  text: string;
  onCitationClick?: (payload: CitationClickPayload) => void;
  /** Test-only: render dropdown open by default (SSR snapshot). */
  defaultOpen?: boolean;
}

export function CitationLink({
  text,
  onCitationClick,
  defaultOpen,
}: Props): React.ReactElement {
  const { segments } = parseCitations(text);
  const children: React.ReactNode[] = [];
  let key = 0;
  for (const seg of segments) {
    if (seg.kind === 'text') {
      children.push(
        React.createElement(React.Fragment, { key: `t${key++}` }, seg.text)
      );
    } else if (seg.kind === 'domain') {
      children.push(
        React.createElement(
          'a',
          {
            key: `d${key++}`,
            href: seg.href,
            target: '_blank',
            rel: 'noopener noreferrer',
            style: linkStyle,
            title: 'Mo ' + seg.domain + ' trong tab moi',
          },
          seg.domain
        )
      );
    } else {
      children.push(
        React.createElement(CitationItem, {
          key: `c${key++}`,
          citation: seg.citation,
          defaultOpen,
          onCitationClick,
        })
      );
    }
  }
  return React.createElement(React.Fragment, null, ...children);
}
